import { api } from './client'

/** Rolling look-back window: last 7 / 30 / 90 / 365 days. */
export type DashboardPeriod = 'week' | 'month' | 'quarter' | 'year'

/** Follow-up health — the same five states as the badge on a deal card (lib/dealHealth). */
export type DashboardHealthKey = 'overdue' | 'due_soon' | 'no_follow_up' | 'stalled' | 'on_track'

export interface DashboardParams {
  period: DashboardPeriod
  /** Narrow to one person (must be in `scope.owners`); omit for everyone the caller can see. */
  ownerId?: string | null
  /** Narrow to one pipeline; omit for all. */
  pipelineId?: string | null
}

export interface DashboardPerson {
  id: string
  name: string
}

export interface DashboardScope {
  ownerId: string | null
  pipelineId: string | null
  /** People the caller may narrow to — just themselves for most agents. */
  owners: DashboardPerson[]
}

export interface DashboardKpi {
  openPipelineValue: number
  openDealCount: number
  newDealCount: number
  newDealCountPrevious: number
  wonCount: number
  wonValue: number
  wonCountPrevious: number
  wonValuePrevious: number
  /** won / (won + lost) in the window, or null when nothing closed. */
  winRate: number | null
  winRatePrevious: number | null
  overdueTaskCount: number
}

export interface DashboardStageRow {
  stageId: string
  name: string
  count: number
  value: number
}

export interface DashboardSectionRow {
  /** null gathers stages that belong to no section. */
  sectionId: string | null
  name: string
  color: string | null
  /** Set only when more than one pipeline is in view. */
  pipelineName: string | null
  count: number
  value: number
  /** Only the stages that hold open deals. */
  stages: DashboardStageRow[]
}

export interface DashboardHealthCounts {
  overdue: number
  dueSoon: number
  noFollowUp: number
  stalled: number
  onTrack: number
}

export interface DashboardAttentionDeal {
  dealId: string
  title: string
  value: number | null
  ownerName: string | null
  health: DashboardHealthKey
  reason: string
}

export interface DashboardAttention {
  /** Every open deal that needs action; `deals` is capped. */
  total: number
  deals: DashboardAttentionDeal[]
}

export interface DashboardTrendMonth {
  /** yyyy-MM, agency time. */
  month: string
  newDealCount: number
  newDealValue: number
  wonCount: number
  wonValue: number
}

export interface DashboardTeamRow {
  /** null for the "Unassigned" row (admin view of everyone only). */
  userId: string | null
  name: string
  openDealCount: number
  openPipelineValue: number
  newDealCount: number
  wonCount: number
  wonValue: number
  winRate: number | null
  overdueTaskCount: number
}

export interface Dashboard {
  period: DashboardPeriod
  periodDays: number
  scope: DashboardScope
  kpi: DashboardKpi
  sections: DashboardSectionRow[]
  health: DashboardHealthCounts
  attention: DashboardAttention
  trend: DashboardTrendMonth[]
  team: DashboardTeamRow[]
  /** Emails / SMS / calls / notes / meetings logged by the people in view, this window and the one before. */
  activity?: DashboardActivity
}

export interface DashboardActivity {
  emails: number
  sms: number
  calls: number
  notes: number
  meetings: number
  emailsPrevious: number
  smsPrevious: number
  callsPrevious: number
  notesPrevious: number
  meetingsPrevious: number
}

export async function getDashboard({ period, ownerId, pipelineId }: DashboardParams): Promise<Dashboard> {
  const { data } = await api.get<Dashboard>('/api/dashboard', {
    params: { period, ownerId: ownerId || undefined, pipelineId: pipelineId || undefined },
  })
  return data
}
